import {
  getTreeExpandedState,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
  Tree,
  TreeNodeData,
  useMantineTheme,
  useTree,
} from '@mantine/core'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import RootButton from '@/components/Buttons/RootButton'
import UserDetails from '@/components/Details/UserDetails/UserDetails'
import classes from '@/components/Tables/WithdrawTable.module.css'
import { useElectionsContext } from '@/contexts/ElectionsContext'
import { useGovernanceContext } from '@/contexts/GovernanceContext'
import { useElectionResults } from '@/hooks/useElectionResults'
import { processBalance, ProcessedBalance } from '@/utils/utils'

import RenderNode from './utils'

interface VoteDetailsProps {
  nominee: string
  close: () => void
}

const VoteDetailsModal: React.FC<VoteDetailsProps> = ({ nominee, close }) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()

  const { electionData } = useGovernanceContext()
  const { idx } = electionData
  const { userUnsplitVotes, userVoteTotals, candidateVoteTotals } = useElectionsContext()
  const { userTotal } = useElectionResults(idx)

  const candidateOtherVotes = useMemo(
    () => processBalance(candidateVoteTotals[nominee].value - userVoteTotals[nominee].value),
    [candidateVoteTotals, nominee, userVoteTotals]
  )

  const userOtherVotes = useMemo(
    () => processBalance(userTotal.value - userVoteTotals[nominee].value),
    [userTotal, nominee, userVoteTotals]
  )

  const renderTreeRow = useCallback(
    (text: string, vote: ProcessedBalance) => (
      <Stack style={{ flexDirection: 'row', gap: '30px', alignItems: 'center' }}>
        <Text>{text}</Text>
        <Title order={5} style={{ color: theme.colors.paragraph[0] }}>
          {vote.display} VOTE
        </Title>
      </Stack>
    ),
    []
  )

  const data: TreeNodeData[] = useMemo(
    () => [
      {
        label: renderTreeRow('Root', userTotal),
        value: 'Root',
        children: [
          {
            label: renderTreeRow('Nominee', candidateVoteTotals[nominee]),
            value: 'nominee',
            children: [
              { label: renderTreeRow('Your Votes', userVoteTotals[nominee]), value: 'you' },
              { label: renderTreeRow('Other Votes', candidateOtherVotes), value: 'other votes' },
            ],
          },
          {
            label: renderTreeRow('Other Candidates', userOtherVotes),
            value: 'other candidates',
          },
        ],
      },
    ],
    [
      renderTreeRow,
      userVoteTotals,
      userTotal,
      candidateVoteTotals,
      nominee,
      candidateOtherVotes,
      userOtherVotes,
    ]
  )

  const tree = useTree({
    initialExpandedState: getTreeExpandedState(data, '*'),
  })

  const rows = useMemo(
    () =>
      userUnsplitVotes
        .filter(row => row.candidate === nominee)
        .map(row => (
          <Table.Tr key={row.timestamp}>
            <Table.Td className={classes.tableRow}>{row.timestamp}</Table.Td>
            <Table.Td className={classes.tableRow}>{row.amount.display}</Table.Td>
          </Table.Tr>
        )),
    [userUnsplitVotes]
  )

  return (
    <Stack key={nominee}>
      <UserDetails userId={nominee} width="fit-content" />
      {/* // TODO: Display user votes */}
      <Tree
        style={{
          backgroundColor: theme.colors.background3[0],
          padding: '10px',
          borderRadius: '8px',
        }}
        data={data}
        tree={tree}
        levelOffset={23}
        renderNode={({ node, level, elementProps }) => (
          <RenderNode node={node} level={level} elementProps={elementProps} data={data} />
        )}
      />
      <Table verticalSpacing={0} stickyHeader>
        <ScrollArea h={200}>
          <Table.Thead className={classes.tableHeader}>
            <Table.Tr>
              <Table.Th className={classes.tableHeader}>Timestamp</Table.Th>
              <Table.Th className={classes.tableHeader}>Amount</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </ScrollArea>
      </Table>
      <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
        <RootButton onClick={close} secondary style1={false} expand>
          {t('buttons.close', 'Close')}
        </RootButton>
      </Stack>
    </Stack>
  )
}

export default VoteDetailsModal
